import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const DEFAULT_KEY_FACTS = [
  { label: "Investment Manager", value: "Drummond Capital Partners Pty Ltd" },
  { label: "Asset Class", value: "Global Multi Asset (Diversified)" },
  { label: "Investment Style", value: "Active" },
  { label: "Benchmark Index", value: "FE AMI Growth" },
  { label: "Growth exposure range (net)", value: "60-80%" },
  { label: "Estimated number of holdings", value: "15-20 managed funds\n2000+ underlying securities" },
  { label: "Inception Date", value: "1st November 2019" },
  { label: "Minimum Investment", value: "$25,000" },
  { label: "Minimum Investment Horizon", value: "7 years" },
  { label: "Drummond Investment Management Fee", value: "0.15% (plus GST)" },
  { label: "Responsible Entity/ MDA fee", value: "0.10%" },
  { label: "Indirect Costs (net of rebates)", value: "0.50%" },
  { label: "Transactional Costs", value: "0.10%" },
  { label: "Performance fees of underlying managers", value: "0.10%" },
];

const DEFAULT_KEY_FACTS_FOOTNOTES = "";

// Word limits matching backend FIELD_LIMITS
const FIELD_LIMITS = {
  quote_text: { min: 15, max: 35, target: "15-25 words" },
  intro_text: { min: 50, max: 90, target: "60-70 words" },
};

// Page-1 left column: 1-3 {header, body} sections sharing ONE word budget, since the
// A4 page clips silently and every extra line pushes the Neutral Asset Allocation
// chart down. Calibrated on rendered PDFs (Sep 2026): two sections fit up to ~200 words
// (DPM001SMA, 201 words + a 4-line quote, only just), three sections at 164 words fit
// comfortably. Each section beyond two costs ~15 words of height for its heading.
const MAX_LEFT_SECTIONS = 3;
const leftSectionLimits = (n) => {
  const extraHeads = Math.max(0, n - 2) * 15;
  return { min: 60, max: 200 - extraHeads, target: `${120 - extraHeads}-${160 - extraHeads} words` };
};

const countWords = (text) => {
  if (!text) return 0;
  return text.trim().split(/\s+/).filter(w => w.length > 0).length;
};

const getWordCountStatus = (fieldName, text, limitsOverride) => {
  const limits = limitsOverride || FIELD_LIMITS[fieldName];
  if (!limits) return null;

  const count = countWords(text);
  let status = 'good';
  let colour = '#155724'; // green

  if (count < limits.min) {
    status = 'short';
    colour = '#856404'; // warning
  } else if (count > limits.max) {
    status = 'long';
    colour = '#721c24'; // danger
  } else if (count > limits.max - 5) {
    status = 'warning';
    colour = '#856404';
  }

  return { count, status, colour, target: limits.target };
};

const WordCount = ({ fieldName, text }) => {
  const info = getWordCountStatus(fieldName, text);
  if (!info) return null;

  return (
    <small style={{ color: info.colour }}>
      {info.count} words (target: {info.target})
    </small>
  );
};

// Show/hide toggle + editable text for a table heading or footer
const LabelEditor = ({ id, title, show, onShowChange, text, onTextChange, placeholder, hint }) => (
  <div className="mb-2">
    <div className="form-check form-switch mb-1">
      <input
        className="form-check-input"
        type="checkbox"
        role="switch"
        id={id}
        checked={show}
        onChange={(e) => onShowChange(e.target.checked)}
      />
      <label className="form-check-label fw-bold" htmlFor={id} style={{ fontSize: '0.8rem' }}>
        {title}
      </label>
    </div>
    <input
      type="text"
      className="form-control form-control-sm"
      value={text}
      onChange={(e) => onTextChange(e.target.value)}
      placeholder={placeholder}
      disabled={!show}
    />
    {hint && <small className="text-muted d-block mt-1">{hint}</small>}
  </div>
);

const DEFAULT_WHOLESALE_TITLE = 'Wholesale clients only';
const DEFAULT_WHOLESALE_TEXT =
  'This document is intended solely for wholesale clients as defined in section 761G of the Corporations Act 2001 (Cth). It is not intended for retail clients and must not be relied on by retail investors. The information is general in nature and does not take into account your objectives, financial situation or needs.';

const EditableContentPanel = ({ portfolioCode, domain, currentContent, onContentSaved }) => {
  const [quoteText, setQuoteText] = useState('');
  const [introText, setIntroText] = useState('');
  const [sections, setSections] = useState([]);   // left column [{header, body}], 1-3
  // Which key-fact lines the backend fills in on every render ('registry' / 'live')
  const keyFactSources = currentContent?.key_fact_sources || {};
  const [keyFacts, setKeyFacts] = useState([]);
  const [keyFactsFootnotes, setKeyFactsFootnotes] = useState('');
  const [importantNotice, setImportantNotice] = useState('');
  const [wholesaleTitle, setWholesaleTitle] = useState(DEFAULT_WHOLESALE_TITLE);
  const [wholesaleText, setWholesaleText] = useState(DEFAULT_WHOLESALE_TEXT);
  // Optional, editable headers/footers for the two performance tables
  const [perfHeading, setPerfHeading] = useState('');
  const [perfHeadingShow, setPerfHeadingShow] = useState(true);
  const [perfFooter, setPerfFooter] = useState('');
  const [perfFooterShow, setPerfFooterShow] = useState(false);
  const [fyHeading, setFyHeading] = useState('');
  const [fyHeadingShow, setFyHeadingShow] = useState(true);
  const [fyFooter, setFyFooter] = useState('');
  const [fyFooterShow, setFyFooterShow] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);

  // Initialize content from currentContent
  useEffect(() => {
    if (currentContent) {
      setQuoteText(currentContent.quote_text || '');
      setIntroText(currentContent.intro_text || '');
      // The backend always resolves left_sections (legacy records included)
      setSections((currentContent.left_sections || []).map((s) => ({ header: s.header, body: s.body })));
      setKeyFacts(currentContent.key_facts || DEFAULT_KEY_FACTS);
      setKeyFactsFootnotes(currentContent.key_facts_footnotes || DEFAULT_KEY_FACTS_FOOTNOTES);
      setImportantNotice(currentContent.important_notice || '');
      // Nullish (not ||) so an intentionally cleared (empty) value stays cleared
      setWholesaleTitle(currentContent.wholesale_title ?? DEFAULT_WHOLESALE_TITLE);
      setWholesaleText(currentContent.wholesale_text ?? DEFAULT_WHOLESALE_TEXT);
      setPerfHeading(currentContent.perf_table_heading || '');
      setPerfHeadingShow(currentContent.perf_table_heading_show !== false);
      setPerfFooter(currentContent.perf_table_footer || '');
      setPerfFooterShow(currentContent.perf_table_footer_show === true);
      setFyHeading(currentContent.fy_table_heading || '');
      setFyHeadingShow(currentContent.fy_table_heading_show !== false);
      setFyFooter(currentContent.fy_table_footer || '');
      setFyFooterShow(currentContent.fy_table_footer_show !== false);
    }
  }, [currentContent]);

  const handleKeyFactChange = (index, field, value) => {
    const updated = [...keyFacts];
    updated[index] = { ...updated[index], [field]: value };
    setKeyFacts(updated);
  };

  const handleAddKeyFact = () => {
    setKeyFacts([...keyFacts, { label: '', value: '' }]);
  };

  const handleRemoveKeyFact = (index) => {
    setKeyFacts(keyFacts.filter((_, i) => i !== index));
  };

  // Left-column sections: edit / add / remove / reorder
  const updateSection = (i, field, value) =>
    setSections((prev) => prev.map((s, j) => (j === i ? { ...s, [field]: value } : s)));
  const addSection = () =>
    setSections((prev) => (prev.length < MAX_LEFT_SECTIONS ? [...prev, { header: '', body: '' }] : prev));
  const removeSection = (i) =>
    setSections((prev) => (prev.length > 1 ? prev.filter((_, j) => j !== i) : prev));
  const moveSection = (i, dir) => setSections((prev) => {
    const j = i + dir;
    if (j < 0 || j >= prev.length) return prev;
    const next = [...prev];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });
  const leftWordInfo = getWordCountStatus(null, sections.map((s) => s.body).join(' '),
                                          leftSectionLimits(sections.length));

  const handleSave = async () => {
    if (!quoteText.trim()) {
      setSaveMessage({ type: 'error', text: 'Quote text cannot be empty' });
      return;
    }
    if (!introText.trim()) {
      setSaveMessage({ type: 'error', text: 'Introduction text cannot be empty' });
      return;
    }
    if (!sections.length) {
      setSaveMessage({ type: 'error', text: 'The left column needs at least one section' });
      return;
    }
    const incomplete = sections.findIndex((s) => !s.header.trim() || !s.body.trim());
    if (incomplete >= 0) {
      setSaveMessage({ type: 'error', text: `Section ${incomplete + 1} needs both a header and body text` });
      return;
    }

    setIsSaving(true);
    setSaveMessage(null);

    try {
      const domainParam = domain ? `?domain=${encodeURIComponent(domain)}` : '';
      const payload = {
        quote_text: quoteText,
        intro_text: introText,
        left_sections: sections.map((s) => ({ header: s.header.trim(), body: s.body.trim() })),
        key_facts: keyFacts,
        key_facts_footnotes: keyFactsFootnotes,
        important_notice: importantNotice,
        wholesale_title: wholesaleTitle,
        wholesale_text: wholesaleText,
        perf_table_heading: perfHeading,
        perf_table_heading_show: perfHeadingShow,
        perf_table_footer: perfFooter,
        perf_table_footer_show: perfFooterShow,
        fy_table_heading: fyHeading,
        fy_table_heading_show: fyHeadingShow,
        fy_table_footer: fyFooter,
        fy_table_footer_show: fyFooterShow,
      };
      await api.put(`/api/factsheets/admin/content/${portfolioCode}${domainParam}`, payload);

      setSaveMessage({ type: 'success', text: 'Content saved successfully!' });
      if (onContentSaved) {
        // Pass saved content for immediate optimistic update
        onContentSaved(payload);
      }
    } catch (err) {
      console.error('Error saving content:', err);
      setSaveMessage({
        type: 'error',
        text: err.response?.data?.detail || 'Failed to save content',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleRevertToDefaults = async () => {
    const revertMsg = domain
      ? 'Are you sure you want to revert to default content? This will delete the brand-specific content and fall back to the Drummond default.'
      : 'Are you sure you want to revert to default content? This will delete all custom content for this portfolio.';
    if (!window.confirm(revertMsg)) {
      return;
    }

    setIsSaving(true);
    setSaveMessage(null);

    try {
      const domainParam = domain ? `?domain=${encodeURIComponent(domain)}` : '';
      await api.delete(`/api/factsheets/admin/content/${portfolioCode}${domainParam}`);
      setSaveMessage({ type: 'success', text: 'Reverted to default content. Refreshing...' });

      setTimeout(() => {
        if (onContentSaved) {
          onContentSaved();
        }
      }, 1000);
    } catch (err) {
      console.error('Error reverting content:', err);
      setSaveMessage({
        type: 'error',
        text: err.response?.data?.detail || 'Failed to revert content',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="card shadow-sm sticky-top" style={{ top: '20px' }}>
      <div className="card-header bg-primary text-white">
        <h5 className="card-title mb-0">
          <i className="bi bi-pencil-square me-2"></i>
          Edit Content
        </h5>
      </div>
      <div className="card-body" style={{ maxHeight: '85vh', overflowY: 'auto' }}>
        <p className="text-muted small mb-3">
          Customise the content for this fact sheet. Changes apply to the selected portfolio.
          Press Enter once for a line break. Press Enter twice for a new paragraph.
        </p>

        {/* Inheritance indicator when viewing a brand that hasn't customised content */}
        {domain && currentContent?.content_domain !== domain && (
          <div className="alert alert-info small py-2 mb-3">
            <i className="bi bi-info-circle me-1"></i>
            Content inherited from Drummond default — saving will create a custom version for this brand.
          </div>
        )}

        {saveMessage && (
          <div className={`alert alert-${saveMessage.type === 'success' ? 'success' : 'danger'} alert-dismissible fade show`} role="alert">
            {saveMessage.text}
            <button
              type="button"
              className="btn-close"
              onClick={() => setSaveMessage(null)}
              aria-label="Close"
            ></button>
          </div>
        )}

        {/* Quote Text */}
        <div className="mb-3">
          <label htmlFor="quoteText" className="form-label fw-bold">
            Quote Text
          </label>
          <textarea
            id="quoteText"
            className="form-control"
            rows="3"
            value={quoteText}
            onChange={(e) => setQuoteText(e.target.value)}
            placeholder="Enter quote text..."
          />
          <WordCount fieldName="quote_text" text={quoteText} />
        </div>

        {/* Intro Text */}
        <div className="mb-3">
          <label htmlFor="introText" className="form-label fw-bold">
            Introduction Text
          </label>
          <textarea
            id="introText"
            className="form-control"
            rows="4"
            value={introText}
            onChange={(e) => setIntroText(e.target.value)}
            placeholder="Enter introduction text..."
          />
          <WordCount fieldName="intro_text" text={introText} />
        </div>

        {/* Left-column sections: 1-3 custom header + body blocks */}
        <div className="mb-3">
          <label className="form-label fw-bold mb-1">Left Column Sections</label>
          <p className="text-muted small mb-2">
            Up to {MAX_LEFT_SECTIONS} sections, each with its own header (displayed in uppercase) and body text.
          </p>

          {sections.map((s, i) => (
            <div key={i} className="mb-2 p-2 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
              <div className="d-flex align-items-center gap-1 mb-1">
                <small className="fw-bold text-muted me-auto">Section {i + 1}</small>
                <button type="button" className="btn btn-sm btn-outline-secondary py-0 px-1"
                        onClick={() => moveSection(i, -1)} disabled={i === 0} title="Move up">
                  <i className="bi bi-arrow-up"></i>
                </button>
                <button type="button" className="btn btn-sm btn-outline-secondary py-0 px-1"
                        onClick={() => moveSection(i, 1)} disabled={i === sections.length - 1} title="Move down">
                  <i className="bi bi-arrow-down"></i>
                </button>
                <button type="button" className="btn btn-sm btn-outline-danger py-0 px-1"
                        onClick={() => removeSection(i)} disabled={sections.length <= 1}
                        title={sections.length <= 1 ? 'At least one section is required' : 'Remove section'}>
                  <i className="bi bi-trash"></i>
                </button>
              </div>
              <input
                type="text"
                className="form-control form-control-sm mb-1"
                value={s.header}
                maxLength={100}
                onChange={(e) => updateSection(i, 'header', e.target.value)}
                placeholder="Header, e.g. DRUMMOND CAPITAL PARTNERS"
              />
              <textarea
                className="form-control"
                rows="4"
                value={s.body}
                onChange={(e) => updateSection(i, 'body', e.target.value)}
                placeholder="Body text..."
              />
              <small className="text-muted">{countWords(s.body)} words</small>
            </div>
          ))}

          {sections.length < MAX_LEFT_SECTIONS && (
            <button type="button" className="btn btn-sm btn-outline-primary mb-2" onClick={addSection}>
              <i className="bi bi-plus-lg me-1"></i>Add section
            </button>
          )}

          {leftWordInfo && (
            <small className="d-block" style={{ color: leftWordInfo.colour }}>
              Left column: {leftWordInfo.count} words across {sections.length} section{sections.length === 1 ? '' : 's'} (target: {leftWordInfo.target})
              {leftWordInfo.status === 'long' && '. Over budget: the Neutral Asset Allocation chart may be pushed off the page.'}
            </small>
          )}
        </div>

        <hr />

        {/* Key Facts Editor */}
        <div className="mb-3">
          <label className="form-label fw-bold">Key Facts</label>
          <p className="text-muted small mb-2">
            Edit labels and values. Use \n in values for multi-line display. Values marked
            "From registry" or "Live" are filled in automatically every time the fact sheet is built.
          </p>

          {keyFacts.map((fact, index) => {
            const source = keyFactSources[fact.label];
            const locked = source === 'registry' || source === 'live';
            return (
            <div key={index} className="mb-2 p-2 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
              <div className="d-flex align-items-start gap-2">
                <div className="flex-grow-1">
                  <input
                    type="text"
                    className="form-control form-control-sm mb-1"
                    placeholder="Label"
                    value={fact.label}
                    onChange={(e) => handleKeyFactChange(index, 'label', e.target.value)}
                  />
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Value"
                    value={fact.value}
                    readOnly={locked}
                    style={locked ? { backgroundColor: '#EDF5F4' } : undefined}
                    onChange={(e) => handleKeyFactChange(index, 'value', e.target.value)}
                  />
                  {source === 'registry' && (
                    <small className="d-block mt-1" style={{ color: '#337B91' }}>
                      <i className="bi bi-link-45deg me-1"></i>From registry: change it in Investment Config
                    </small>
                  )}
                  {source === 'live' && (
                    <small className="d-block mt-1" style={{ color: '#337B91' }}>
                      <i className="bi bi-lightning-charge me-1"></i>Live: calculated from current portfolio data
                    </small>
                  )}
                  {source === 'registry_blank' && (
                    <small className="d-block mt-1" style={{ color: '#856404' }}>
                      <i className="bi bi-exclamation-triangle me-1"></i>Not set in the registry, so this text is used. Add it in Investment Config to keep it in sync.
                    </small>
                  )}
                </div>
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => handleRemoveKeyFact(index)}
                  title="Remove row"
                  style={{ minWidth: '32px' }}
                >
                  <i className="bi bi-trash"></i>
                </button>
              </div>
            </div>
            );
          })}

          <button
            type="button"
            className="btn btn-outline-primary btn-sm mt-1"
            onClick={handleAddKeyFact}
          >
            <i className="bi bi-plus-lg me-1"></i>
            Add Row
          </button>
        </div>

        {/* Key Facts Footnotes */}
        <div className="mb-3">
          <label htmlFor="keyFactsFootnotes" className="form-label fw-bold">
            Key Facts Footnotes
          </label>
          <textarea
            id="keyFactsFootnotes"
            className="form-control"
            rows="3"
            value={keyFactsFootnotes}
            onChange={(e) => setKeyFactsFootnotes(e.target.value)}
            placeholder="Enter footnotes (one per line)..."
          />
        </div>

        <hr />

        {/* Performance Table Headings & Footers */}
        <div className="mb-3">
          <label className="form-label fw-bold">Performance Tables</label>
          <p className="text-muted small mb-2">
            Toggle and customise the heading above and footer below each performance table.
            Leave blank to use the automatic default.
          </p>

          <div className="p-2 border rounded mb-2" style={{ backgroundColor: '#f8f9fa' }}>
            <div className="small text-uppercase text-muted mb-1">Performance table</div>
            <LabelEditor
              id="perfHeadingShow"
              title="Heading"
              show={perfHeadingShow}
              onShowChange={setPerfHeadingShow}
              text={perfHeading}
              onTextChange={setPerfHeading}
              placeholder="Performance"
              hint="The report date is added automatically after this text (e.g. “… – 30 April 2026”)."
            />
            <LabelEditor
              id="perfFooterShow"
              title="Footer"
              show={perfFooterShow}
              onShowChange={setPerfFooterShow}
              text={perfFooter}
              onTextChange={setPerfFooter}
              placeholder="Footer text (optional)"
            />
          </div>

          <div className="p-2 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
            <div className="small text-uppercase text-muted mb-1">Financial year table</div>
            <LabelEditor
              id="fyHeadingShow"
              title="Heading"
              show={fyHeadingShow}
              onShowChange={setFyHeadingShow}
              text={fyHeading}
              onTextChange={setFyHeading}
              placeholder="Default: Performance - Financial Year"
            />
            <LabelEditor
              id="fyFooterShow"
              title="Footer"
              show={fyFooterShow}
              onShowChange={setFyFooterShow}
              text={fyFooter}
              onTextChange={setFyFooter}
              placeholder="Blank: managed text (Admin › Disclaimers › Performance table footer)"
              hint="Only for wording specific to this portfolio. Text here replaces the managed footer on this fact sheet and its quarterly report; a blank brand copy inherits the Drummond copy's text."
            />
          </div>
        </div>

        <hr />

        {/* Wholesale disclaimer (page 1) */}
        <div className="mb-3">
          <label htmlFor="wholesaleTitle" className="form-label fw-bold">
            Wholesale Disclaimer (page 1)
          </label>
          <p className="text-muted small mb-2">
            The &ldquo;wholesale clients only&rdquo; notice shown at the bottom of page 1.
          </p>
          <input
            id="wholesaleTitle"
            type="text"
            className="form-control mb-2"
            value={wholesaleTitle}
            onChange={(e) => setWholesaleTitle(e.target.value)}
            placeholder="Heading (e.g. Wholesale clients only)"
          />
          {/* The notice text itself is managed centrally (Admin > Disclaimers,
              "Fact sheet wholesale notice") and follows the viewer's brand.
              Here it can only be shown or hidden for this fact sheet; hidden
              is stored as an empty string, as before. */}
          <div className="form-check">
            <input
              id="wholesaleText"
              type="checkbox"
              className="form-check-input"
              checked={wholesaleText !== ''}
              onChange={(e) => setWholesaleText(e.target.checked ? DEFAULT_WHOLESALE_TEXT : '')}
            />
            <label htmlFor="wholesaleText" className="form-check-label">
              Show the wholesale notice on this fact sheet
            </label>
          </div>
          <p className="text-muted small mt-1 mb-0">
            The notice text is managed in{' '}
            <a href="/admin?tab=disclaimers" target="_blank" rel="noopener noreferrer">Admin › Disclaimers</a>.
          </p>
        </div>

        {/* Important Notice */}
        <div className="mb-3">
          <label htmlFor="importantNotice" className="form-label fw-bold">
            Important Notice
          </label>
          <p className="text-muted small mb-2">
            Edit the important notice text on page 2. Use a blank line between paragraphs.
          </p>
          <textarea
            id="importantNotice"
            className="form-control"
            rows="8"
            value={importantNotice}
            onChange={(e) => setImportantNotice(e.target.value)}
            placeholder="Enter important notice text..."
          />
        </div>

        {/* Action Buttons */}
        <div className="d-grid gap-2">
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Saving...
              </>
            ) : (
              <>
                <i className="bi bi-save me-2"></i>
                Save Content
              </>
            )}
          </button>
          <button
            className="btn btn-outline-secondary"
            onClick={handleRevertToDefaults}
            disabled={isSaving}
          >
            <i className="bi bi-arrow-counterclockwise me-2"></i>
            Revert to Default
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditableContentPanel;
